import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useParams, useSearch } from "@tanstack/react-router";
import type { ReactElement, ReactNode } from "react";
import { authenticatedApi } from "@/lib/api";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { RdChatsCardSlot } from "@/components/v2/rd-chats-card-slot";
import { RdContentTabs } from "@/components/v2/rd-content-tabs";
import { RdHeaderStrip } from "@/components/v2/rd-header-strip";
import { RdPageFrame } from "@/components/v2/rd-page-frame";
import { RdWorkspaceSidebar } from "@/components/v2/rd-workspace-sidebar";
import { useShellFrame } from "@/components/v2/shell-layout";
import { SocialNotesBanner } from "@/components/v2/social-notes-banner";
import type { FormProfileData } from "@/components/v2/forms-profile-card";
import {
  sessionListRefetchInterval,
  type SessionRowData,
} from "@/components/v2/session-row";
import type { SupervisorOption } from "@/components/v2/meeting-note-form";
import type {
  MeetingNoteData,
  MeetingNoteShareRecipientOption,
} from "@/components/v2/meeting-note-types";
import type { ObservationNoteData } from "../../../shared/observation-types";
import type { AlwaysAiUser, WorkspaceSummaryResponse } from "../../../shared/api-types";
import { SOCIAL_NOTES_FLAG, useFeatureEnabled } from "@/lib/use-features";
import { can, canRenameRd } from "@/lib/permissions";
import type { RdDetailData } from "@/lib/use-rd-detail";
import { useRdOptions } from "@/lib/use-rd-options";

/**
 * The rd-workspace shell layout that wraps every per-surface route
 * (/rds/$rdId itself when the supervisor-metrics flag is on, plus
 * /rds/$rdId/{scorecards,observations,meetings,forms,chats,…}). The classic
 * frame renders the RdWorkspaceSidebar beside the surface Outlet; the
 * single-rail frame (`always-brand`, NO-754) renders the RD page header and
 * tabs above it instead.
 */
export function RdWorkspaceLayout(): ReactElement | null {
  return useShellFrame() === "rail" ? (
    <RailRdWorkspaceLayout />
  ) : (
    <ClassicRdWorkspaceLayout />
  );
}

/** The sidebar's record and thread bootstrap, and the RD switcher's list. */
function useRdWorkspaceQueries(rdId: string) {
  const { getToken } = useAuth();
  const workspace = useQuery({
    queryKey: ["workspace-summary", rdId],
    queryFn: () =>
      authenticatedApi<{ data: WorkspaceSummaryResponse }>(
        getToken,
        `/api/rds/${rdId}/chats`
      ),
    placeholderData: (previous) => previous,
  });
  const rdOptions = useRdOptions();

  return { workspace, rdOptions };
}

function RailRdWorkspaceLayout(): ReactElement {
  const { rdId } = useParams({ strict: false }) as { rdId: string };
  const { rd, me } = useSurfaceBase(rdId);
  const { workspace, rdOptions } = useRdWorkspaceQueries(rdId);

  return (
    <RdPageFrame
      rdParam={rdId}
      detail={rd.data?.data}
      detailFailed={rd.isError}
      user={me.data?.user}
      summary={workspace.data?.data}
      rdOptions={rdOptions.data?.data ?? []}
    />
  );
}

function ClassicRdWorkspaceLayout(): ReactElement {
  const { rdId } = useParams({ strict: false }) as { rdId: string };
  const { workspace, rdOptions } = useRdWorkspaceQueries(rdId);

  const summary = workspace.data?.data;

  return (
    <div className="flex h-full min-h-0">
      {summary && (
        <RdWorkspaceSidebar
          rdId={summary.rdId}
          rdSlug={summary.rdSlug}
          rdName={summary.rdName}
          rdOptions={
            rdOptions.data?.data ?? [
              { id: summary.rdId, slug: summary.rdSlug, name: summary.rdName },
            ]
          }
          sessionCount={summary.sessionCount}
          observationCount={summary.observationCount}
          meetingNoteCount={summary.meetingNoteCount}
          formSnapshotCount={summary.formSnapshotCount}
          threads={summary.threads}
          otherOwners={summary.otherOwners}
        />
      )}
      <main className="flex-1 min-w-0 min-h-0 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}

/** Shared per-surface data: the RD detail + the current user. */
export function useSurfaceBase(rdId: string) {
  const { getToken } = useAuth();
  const rd = useQuery({
    queryKey: ["rd", rdId],
    queryFn: () =>
      authenticatedApi<{ data: RdDetailData }>(getToken, `/api/rds/${rdId}`),
    retry: false,
  });
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });

  return { getToken, rd, me };
}

/** Max-w-[920px] scroll container + breadcrumb tail for a workspace surface. */
export function SurfaceShell({
  detail,
  surfaceLabel,
  children,
}: {
  detail: RdDetailData;
  surfaceLabel: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="h-full overflow-y-auto">
      {/* Under the always theme the column widens by its padding, so the
          cards share the frame header's and tabs' left edge (NO-792) and
          keep the gutter on a narrow screen. */}
      <div className="max-w-[920px] mx-auto px-6 py-6 flex flex-col gap-4 brand:max-w-[968px]">
        <BreadcrumbSetter
          segments={[
            { label: "RDs", href: "/rds" },
            { label: detail.name, href: `/rds/${detail.slug}` },
            { label: surfaceLabel },
          ]}
        />
        {children}
      </div>
    </div>
  );
}

export function surfaceHeader(
  rdId: string,
  detail: RdDetailData,
  canEdit: boolean
): ReactElement {
  return (
    <RdHeaderStrip
      rdId={rdId}
      canEdit={canEdit}
      initialName={detail.name}
      sessionCount={detail.sessionCount}
      createdAt={detail.createdAt}
      supervisorName={detail.supervisorName}
      healthieLink={detail.healthieLink}
    />
  );
}

export function SurfaceStatus({
  rd,
}: {
  rd: ReturnType<typeof useSurfaceBase>["rd"];
}): ReactElement {
  if (rd.isError) {
    return (
      <QueryErrorState
        error={rd.error}
        notFoundText="RD not found"
        onRetry={() => void rd.refetch()}
        className="h-full"
      />
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-muted-foreground">Loading...</p>
    </div>
  );
}

/**
 * Session Scorecards — the workspace landing surface. With supervisor-metrics
 * off there is no Overview, so in the rail frame the Chats card leads it
 * (NO-754).
 */
export function ScorecardsSurface(): ReactElement {
  const { rdId } = useParams({ strict: false }) as { rdId: string };
  const { getToken, rd, me } = useSurfaceBase(rdId);
  const sessions = useQuery({
    queryKey: ["rd-sessions", rdId],
    queryFn: () =>
      authenticatedApi<{ data: SessionRowData[] }>(
        getToken,
        `/api/rds/${rdId}/sessions`
      ),
    // NO-775: slow re-read while a pasted Session waits for Healthie.
    refetchInterval: (query) => sessionListRefetchInterval(query.state.data?.data),
  });
  const detail = rd.data?.data;
  if (!detail || !me.data || !sessions.data) return <SurfaceStatus rd={rd} />;

  return (
    <SurfaceShell detail={detail} surfaceLabel="Session Scorecards">
      <RdChatsCardSlot rdParam={rdId} rdName={detail.name} surface="sessions" />
      <RdContentTabs
        chrome="surface"
        header={surfaceHeader(rdId, detail, canRenameRd(me.data.user))}
        rdId={rdId}
        rdSlug={detail.slug}
        rdName={detail.name}
        sessions={sessions.data.data}
        observations={[]}
        meetingNotes={[]}
        meetingNoteShareRecipientOptions={[]}
        viewerRole={me.data.user.role}
        currentUserId={me.data.user.id}
        currentUserName={me.data.user.name}
        rdSupervisorUserId={detail.supervisedByUserId}
        rdSupervisorName={detail.supervisorName}
        supervisorOptions={[]}
        fixedTab="sessions"
      />
    </SurfaceShell>
  );
}

/** Observations — the shipped observations tab in surface chrome. */
export function ObservationsSurface(): ReactElement {
  const { rdId } = useParams({ strict: false }) as { rdId: string };
  const { getToken, rd, me } = useSurfaceBase(rdId);
  const observations = useQuery({
    queryKey: ["observations", rdId],
    queryFn: () =>
      authenticatedApi<{ data: ObservationNoteData[] }>(
        getToken,
        `/api/rds/${rdId}/observations`
      ),
  });
  const detail = rd.data?.data;
  if (!detail || !me.data || !observations.data) return <SurfaceStatus rd={rd} />;

  return (
    <SurfaceShell detail={detail} surfaceLabel="Observations">
      <RdContentTabs
        chrome="surface"
        header={surfaceHeader(rdId, detail, canRenameRd(me.data.user))}
        rdId={rdId}
        rdSlug={detail.slug}
        rdName={detail.name}
        sessions={[]}
        observations={observations.data.data}
        meetingNotes={[]}
        meetingNoteShareRecipientOptions={[]}
        viewerRole={me.data.user.role}
        currentUserId={me.data.user.id}
        currentUserName={me.data.user.name}
        rdSupervisorUserId={detail.supervisedByUserId}
        rdSupervisorName={detail.supervisorName}
        supervisorOptions={[]}
        fixedTab="observations"
      />
    </SurfaceShell>
  );
}

function useMeetingsQueries(
  rdId: string,
  getToken: ReturnType<typeof useAuth>["getToken"],
  options: { socialNotesEnabled: boolean; isAdmin: boolean }
) {
  const profile = useQuery({
    queryKey: ["forms-profile", rdId],
    queryFn: () =>
      authenticatedApi<{ data: FormProfileData | null }>(
        getToken,
        `/api/rds/${rdId}/forms/profile`
      ),
    enabled: options.socialNotesEnabled,
  });
  const meetingNotes = useQuery({
    queryKey: ["meeting-notes", rdId],
    queryFn: () =>
      authenticatedApi<{ data: MeetingNoteData[] }>(
        getToken,
        `/api/rds/${rdId}/meeting-notes`
      ),
  });
  const shareRecipients = useQuery({
    queryKey: ["meeting-note-share-recipients", rdId],
    queryFn: async (): Promise<MeetingNoteShareRecipientOption[]> => {
      try {
        const response = await authenticatedApi<{
          data: MeetingNoteShareRecipientOption[];
        }>(getToken, `/api/rds/${rdId}/meeting-notes/share-recipients`);

        return response.data;
      } catch {
        return [];
      }
    },
  });
  const supervisors = useQuery({
    queryKey: ["supervisor-options"],
    queryFn: async (): Promise<SupervisorOption[]> => {
      const response = await authenticatedApi<{
        data: { supervisors: SupervisorOption[] };
      }>(getToken, "/api/rds?include=supervisors");

      return response.data.supervisors;
    },
    enabled: options.isAdmin,
  });

  return { profile, meetingNotes, shareRecipients, supervisors };
}

function SocialNotesSlot({
  profile,
  rdSlug,
}: {
  profile: ReturnType<typeof useMeetingsQueries>["profile"];
  rdSlug: string;
}): ReactElement {
  if (profile.isError) {
    return (
      <QueryErrorState
        error={profile.error}
        notFoundText="Social notes aren't available for this RD"
        onRetry={() => void profile.refetch()}
        className="rounded-xl border border-border bg-background px-[14px] py-3"
      />
    );
  }

  return (
    <SocialNotesBanner
      rdSlug={rdSlug}
      socialNotes={profile.data?.data?.socialNotes ?? null}
    />
  );
}

/** Meetings — the shipped Meeting Notes flows in surface chrome. */
export function MeetingsSurface(): ReactElement | null {
  const { rdId } = useParams({ strict: false }) as { rdId: string };
  const search = useSearch({ strict: false }) as { meetingNoteId?: string };
  const initialMeetingNoteId =
    typeof search.meetingNoteId === "string" ? search.meetingNoteId : null;
  const { getToken, rd, me } = useSurfaceBase(rdId);
  const socialNotesEnabled = useFeatureEnabled(SOCIAL_NOTES_FLAG);
  const isAdmin = me.data ? can(me.data.user, "supervisor_attribution:assign") : false;
  const { profile, meetingNotes, shareRecipients, supervisors } = useMeetingsQueries(
    rdId,
    getToken,
    { socialNotesEnabled, isAdmin }
  );

  const detail = rd.data?.data;
  const notesReady =
    meetingNotes.data !== undefined && shareRecipients.data !== undefined;
  const supervisorsReady = !isAdmin || supervisors.data !== undefined;
  const ready = notesReady && supervisorsReady;
  // Wait for the profile query to settle so the banner never flashes its empty
  // state. Errors render the app's retryable state instead of hiding the slot.
  const profileSettled = profile.data !== undefined || profile.isError;
  const socialNotesReady = socialNotesEnabled && profileSettled;
  if (!ready || !detail || !me.data) return <SurfaceStatus rd={rd} />;

  return (
    <SurfaceShell detail={detail} surfaceLabel="Meetings">
      <RdContentTabs
        chrome="surface"
        header={surfaceHeader(rdId, detail, canRenameRd(me.data.user))}
        banner={
          socialNotesReady ? (
            <SocialNotesSlot profile={profile} rdSlug={detail.slug} />
          ) : null
        }
        rdId={rdId}
        rdSlug={detail.slug}
        rdName={detail.name}
        sessions={[]}
        observations={[]}
        meetingNotes={meetingNotes.data?.data ?? []}
        meetingNoteShareRecipientOptions={shareRecipients.data ?? []}
        initialMeetingNoteId={initialMeetingNoteId}
        viewerRole={me.data.user.role}
        currentUserId={me.data.user.id}
        currentUserName={me.data.user.name}
        rdSupervisorUserId={detail.supervisedByUserId}
        rdSupervisorName={detail.supervisorName}
        supervisorOptions={supervisors.data ?? []}
        fixedTab="meeting-notes"
        baseUrl={{ to: "/rds/$rdId/meetings", params: { rdId: detail.slug } }}
      />
    </SurfaceShell>
  );
}
